
import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/shared/hooks/useAuth';
import { CheckCircle2, X } from 'lucide-react';
import { formatDisplayName } from '@/shared/utils/formatters';

interface TransactionRequest {
    id: string;
    userId: string;
    userName: string;
    type: 'deposit' | 'withdrawal';
    currency: string;
    amount: number;
    status: 'pending' | 'account_issued' | 'deposit_claimed' | 'approved' | 'rejected';
    virtualAccount?: {
        bank: string;
        accountNumber: string;
        accountHolder: string;
    };
    userNotified?: boolean;
}

interface PushNotification {
    id: string;
    userId: string;
    title: string;
    message: string;
    read: boolean;
    createdAt: any;
}

const UserNotifications: React.FC = () => {
    const { user } = useAuth();
    const [transactionNotification, setTransactionNotification] = useState<TransactionRequest | null>(null);
    const [pushNotification, setPushNotification] = useState<PushNotification | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    // 1. Transaction Notifications Listener
    useEffect(() => {
        if (!user) return;

        const q = query(
            collection(db, 'transactionRequests'),
            where('userId', '==', user.uid),
            where('userNotified', '!=', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const data = change.doc.data() as TransactionRequest;
                const docId = change.doc.id;

                if (change.type === 'added' || change.type === 'modified') {
                    if ((data.status === 'account_issued' || (data.status === 'approved' && data.type === 'deposit' && data.currency === 'KRW')) && !data.userNotified) {
                        setTransactionNotification({ ...data, id: docId });
                        setIsVisible(true);
                    }
                }
            });
        });

        return () => unsubscribe();
    }, [user]);

    // 2. Admin Push Notifications Listener
    useEffect(() => {
        if (!user) return;

        const q = query(
            collection(db, 'user_push_notifications'),
            where('userId', '==', user.uid),
            where('read', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const data = change.doc.data() as PushNotification;
                const docId = change.doc.id;

                if (change.type === 'added' || change.type === 'modified') {
                    setPushNotification({ ...data, id: docId });
                    setIsVisible(true);
                }
            });
        });

        return () => unsubscribe();
    }, [user]);

    const handleClose = async () => {
        try {
            if (transactionNotification) {
                await updateDoc(doc(db, 'transactionRequests', transactionNotification.id), {
                    userNotified: true
                });
                setTransactionNotification(null);
            }

            if (pushNotification) {
                await updateDoc(doc(db, 'user_push_notifications', pushNotification.id), {
                    read: true
                });
                setPushNotification(null);
            }

            setIsVisible(false);
        } catch (error) {
            console.error("Error marking notification as read:", error);
            setIsVisible(false);
        }
    };

    // Prioritize push notifications if both exist, or just show whatever is available
    const activeNotification = pushNotification || transactionNotification;
    if (!isVisible || !activeNotification) return null;

    const isPush = 'message' in activeNotification;
    const isAccountIssued = !isPush && (activeNotification as TransactionRequest).status === 'account_issued';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <div className="w-full max-w-md bg-card rounded-[2rem] border border-border shadow-2xl overflow-hidden scale-100 animate-in zoom-in-95 duration-300">
                <div className="p-8 text-center space-y-6">
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-blue-600 dark:text-blue-500" />
                    </div>

                    <div className="space-y-2">
                        <h3 className="text-2xl font-black uppercase tracking-tight">
                            {isPush ? (activeNotification as PushNotification).title : (isAccountIssued ? '가상계좌 발급 완료' : '입금 확인 완료')}
                        </h3>
                        <p className="text-muted-foreground font-bold whitespace-pre-wrap">
                            {isPush
                                ? (activeNotification as PushNotification).message
                                : (isAccountIssued
                                    ? '고객님 전용 가상계좌가 발급되었습니다.'
                                    : `₩${(activeNotification as TransactionRequest).amount.toLocaleString()} 입금이 확인되었습니다.`)}
                        </p>
                    </div>

                    {!isPush && isAccountIssued && (activeNotification as TransactionRequest).virtualAccount && (
                        <div className="bg-accent/50 p-6 rounded-2xl space-y-4 text-left border border-border">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">은행</span>
                                <span className="font-bold">{(activeNotification as TransactionRequest).virtualAccount?.bank}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">계좌번호</span>
                                <span className="font-mono font-black text-lg text-blue-600 dark:text-blue-500">
                                    {(activeNotification as TransactionRequest).virtualAccount?.accountNumber}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">예금주</span>
                                <span className="font-bold">{(activeNotification as TransactionRequest).virtualAccount?.accountHolder}</span>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleClose}
                        className="w-full py-4 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-blue-600/20"
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserNotifications;
